import { useCallback, useEffect, useState } from 'react';
import {
  fmt, opsApi,
  type OpsCampaignDto, type OpsMarketingSummaryDto, type OpsRfqDto, type OpsRfqInput, type OpsRfqStatus,
} from '../api';
import { OpsPanelState, OpsTable, RowActions, opsErrorText, useOpsPanel } from './ops/panel';

/**
 * TICK-125 — asking vendors for a price, choosing one, and what the ad spend bought.
 *
 * Owner-only, all of it, and the server says so too: a staff token gets 403 from every route behind
 * this screen. Money leaving the organisation is not the same class of record as a crew list.
 *
 * The number this screen exists to state carefully is ROAS. It is revenue JamTick already owns,
 * divided by a figure an operator typed in — so it is exactly as trustworthy as that figure, and
 * when nobody has entered one it is **null, not zero**. Zero would read as "we spent and got
 * nothing", which is a different claim from "we do not know yet", and the two lead to opposite
 * decisions (TICK-111 D5). The screen prints "ยังไม่มีข้อมูล" rather than a number it cannot stand
 * behind.
 *
 * An RFQ becomes `awarded` by choosing a winning bid, never by picking the status from a menu; the
 * server refuses the status without a winner, and refuses to delete the winning bid while it wins.
 */

const RFQ_STATUS_LABEL: Record<OpsRfqStatus, string> = {
  open: 'เปิดรับราคา',
  awarded: 'ตัดสินแล้ว',
  cancelled: 'ยกเลิก',
};

/*
 * `fmt` already renders the ฿ itself. Wrapping it again printed ฿฿180,000 on every amount this
 * screen shows — the e2e assertion looked for "180,000" and a doubled sign slips straight past a
 * substring check.
 */
const baht = fmt;

export function OpsProcurementPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [rfqs, setRfqs] = useState<OpsRfqDto[] | null>(null);
  const [campaigns, setCampaigns] = useState<OpsCampaignDto[]>([]);
  const [summary, setSummary] = useState<OpsMarketingSummaryDto | null>(null);
  const [loadError, setLoadError] = useState('');
  const [rfqForm, setRfqForm] = useState<{ mode: 'create' } | { mode: 'edit'; rfq: OpsRfqDto } | null>(null);
  const [bidFor, setBidFor] = useState<OpsRfqDto | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, c, s] = await Promise.all([
        opsApi.listRfqs(token, eventSlug),
        opsApi.listCampaigns(token, eventSlug),
        opsApi.marketingSummary(token, eventSlug),
      ]);
      setRfqs(r);
      setCampaigns(c);
      setSummary(s);
    } catch (e) {
      setLoadError(opsErrorText(e, 'โหลดข้อมูลจัดซื้อและการตลาดไม่สำเร็จ'));
    }
  }, [token, eventSlug]);

  useEffect(() => { void load(); }, [load]);

  const { busy, error, run } = useOpsPanel(load);

  if (!rfqs) return <OpsPanelState error={loadError} />;

  return (
    <div className="ops-panel">
      {error && <div className="form-error">{error}</div>}

      <section>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>ใบขอราคา</h3>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--dim)', lineHeight: 1.7 }}>
              ใบขอราคาจะเป็น “ตัดสินแล้ว” ได้ก็ต่อเมื่อเลือกข้อเสนอที่ชนะ — เลือกสถานะเองไม่ได้
            </p>
          </div>
          <button className="btn-primary" disabled={busy} onClick={() => setRfqForm({ mode: 'create' })}>เพิ่มใบขอราคา</button>
        </div>

        {rfqs.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--dim)' }}>ยังไม่มีใบขอราคา</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rfqs.map((r) => (
              <div key={r.id} style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13.5 }}>{r.title}</strong>
                  <span style={{ fontSize: 11.5, color: 'var(--dim)' }}>{r.category} · ปิดรับ {r.deadline}</span>
                  <span style={{ fontSize: 11.5 }}>{RFQ_STATUS_LABEL[r.status]}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--dim)' }}>
                    งบเป้าหมาย {r.budgetTarget === null ? 'ยังไม่ตั้ง' : baht(r.budgetTarget)}
                  </span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, whiteSpace: 'nowrap' }}>
                    <button className="btn-link" disabled={busy} onClick={() => setBidFor(r)}>เพิ่มข้อเสนอ</button>
                    <button className="btn-link" disabled={busy} onClick={() => setRfqForm({ mode: 'edit', rfq: r })}>แก้ไข</button>
                    <button
                      className="btn-link"
                      disabled={busy}
                      onClick={() => {
                        if (!confirm(`ลบใบขอราคา "${r.title}"?`)) return;
                        void run(() => opsApi.deleteRfq(token, r.id, r.version, eventSlug));
                      }}
                    >
                      ลบ
                    </button>
                  </div>
                </div>

                {r.bids.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 8 }}>ยังไม่มีข้อเสนอ</div>
                ) : (
                  <table className="admin-table" style={{ marginTop: 10 }}>
                    <thead>
                      <tr><th>ผู้เสนอ</th><th className="num">ราคา</th><th>หมายเหตุ</th><th /></tr>
                    </thead>
                    <tbody>
                      {r.bids.map((b) => (
                        <tr key={b.id}>
                          <td>
                            {b.vendorName}
                            {r.awardedBidId === b.id && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--sv-ok-soft)' }}>ชนะ</span>}
                          </td>
                          <td className="num" style={{ fontFamily: 'var(--mono)', fontSize: 12.5 }}>{baht(b.totalBidAmount)}</td>
                          <td style={{ color: 'var(--dim)', fontSize: 12 }}>{b.notes || '—'}</td>
                          <td>
                            <RowActions>
                              {r.awardedBidId !== b.id && (
                                <button
                                  className="btn-link"
                                  disabled={busy}
                                  onClick={() => void run(() => opsApi.updateRfq(
                                    token, r.id, { awardedBidId: b.id, status: 'awarded', version: r.version }, eventSlug,
                                  ))}
                                >
                                  เลือกเป็นผู้ชนะ
                                </button>
                              )}
                              <button
                                className="btn-link"
                                disabled={busy}
                                onClick={() => {
                                  if (!confirm(`ลบข้อเสนอของ "${b.vendorName}"?`)) return;
                                  void run(() => opsApi.deleteBid(token, r.id, b.id, b.version, eventSlug));
                                }}
                              >
                                ลบ
                              </button>
                            </RowActions>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}

        {rfqForm && (
          <RfqEditor
            rfq={rfqForm.mode === 'edit' ? rfqForm.rfq : null}
            busy={busy}
            onCancel={() => setRfqForm(null)}
            onSave={async (input) => {
              const ok = await run(() => (rfqForm.mode === 'edit'
                ? opsApi.updateRfq(token, rfqForm.rfq.id, { ...input, version: rfqForm.rfq.version }, eventSlug)
                : opsApi.createRfq(token, input, eventSlug)));
              if (ok) setRfqForm(null);
            }}
          />
        )}

        {bidFor && (
          <BidForm
            rfq={bidFor}
            busy={busy}
            onCancel={() => setBidFor(null)}
            onSave={async (input) => {
              const ok = await run(() => opsApi.addBid(token, bidFor.id, input, eventSlug));
              if (ok) setBidFor(null);
            }}
          />
        )}
      </section>

      <section>
        <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>การตลาด</h3>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--dim)', lineHeight: 1.7 }}>
          รายได้มาจากออเดอร์จริงที่ระบบเป็นเจ้าของ ส่วนค่าโฆษณาเป็นตัวเลขที่กรอกเอง — ROAS จึงเชื่อถือได้เท่าตัวเลขที่กรอก
        </p>

        {summary && (
          <div className="ticket-edit-grid" style={{ marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--dim)' }}>งบโฆษณาที่ตั้งไว้</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 15 }}>{baht(summary.targetAdsSpend)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--dim)' }}>ใช้จริง</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 15 }}>
                {summary.actualAdsSpend === null ? <span style={{ color: 'var(--dim)', fontSize: 13 }}>ยังไม่มีข้อมูล</span> : baht(summary.actualAdsSpend)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--dim)' }}>รายได้ที่ระบบบันทึก</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 15 }}>{baht(summary.attributedRevenue)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--dim)' }}>ROAS</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 15 }}>
                {/* null, not 0 — "ไม่รู้" กับ "ใช้เงินแล้วไม่ได้อะไร" คนละเรื่องกัน */}
                {summary.roas === null
                  ? <span style={{ color: 'var(--dim)', fontSize: 13 }}>ยังไม่มีข้อมูล</span>
                  : `${summary.roas.toFixed(2)}×`}
              </div>
            </div>
          </div>
        )}

        <CampaignTable
          campaigns={campaigns}
          busy={busy}
          onSave={async (id, patch) => { await run(() => opsApi.updateCampaign(token, id, patch, eventSlug)); }}
          onAdd={async (input) => { await run(() => opsApi.createCampaign(token, input, eventSlug)); }}
          onDelete={async (c) => {
            if (!confirm(`ลบแคมเปญ "${c.name}"?`)) return;
            await run(() => opsApi.deleteCampaign(token, c.id, c.version, eventSlug));
          }}
        />
      </section>
    </div>
  );
}

function RfqEditor({ rfq, busy, onCancel, onSave }: {
  rfq: OpsRfqDto | null;
  busy: boolean;
  onCancel: () => void;
  onSave: (input: OpsRfqInput) => void;
}) {
  const [title, setTitle] = useState(rfq?.title ?? '');
  const [category, setCategory] = useState(rfq?.category ?? '');
  const [deadline, setDeadline] = useState(rfq?.deadline ?? '');
  const [budgetTarget, setBudgetTarget] = useState(rfq?.budgetTarget === null || rfq === null ? '' : String(rfq.budgetTarget));

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px', marginTop: 12 }}>
      <h4 style={{ margin: '0 0 12px', fontSize: 13.5 }}>{rfq ? `แก้ไข ${rfq.title}` : 'เพิ่มใบขอราคา'}</h4>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5 }}>
        เรื่องที่ขอราคา
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>

      <div className="ticket-edit-grid" style={{ marginTop: 12 }}>
        <label>
          หมวด
          <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="เช่น เวที, เครื่องเสียง" />
        </label>
        <label>
          ปิดรับราคา
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        </label>
        <label>
          งบเป้าหมาย (บาท)
          <input
            inputMode="numeric"
            value={budgetTarget}
            onChange={(e) => setBudgetTarget(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="เว้นว่าง = ยังไม่ตั้งงบ"
          />
        </label>
      </div>

      <div className="admin-actions" style={{ marginTop: 12 }}>
        <button
          className="btn-primary"
          disabled={busy || !title.trim() || !category.trim() || !deadline}
          onClick={() => onSave({
            title: title.trim(),
            category: category.trim(),
            deadline,
            budgetTarget: budgetTarget.trim() === '' ? null : Number(budgetTarget),
          })}
        >
          บันทึก
        </button>
        <button className="btn-link" onClick={onCancel}>ยกเลิก</button>
      </div>
    </div>
  );
}

function BidForm({ rfq, busy, onCancel, onSave }: {
  rfq: OpsRfqDto;
  busy: boolean;
  onCancel: () => void;
  onSave: (input: { vendorName: string; totalBidAmount: number; notes?: string }) => void;
}) {
  const [vendorName, setVendorName] = useState('');
  const [totalBidAmount, setTotalBidAmount] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px', marginTop: 12 }}>
      <h4 style={{ margin: '0 0 12px', fontSize: 13.5 }}>เพิ่มข้อเสนอให้ “{rfq.title}”</h4>

      <div className="ticket-edit-grid">
        <label>
          ผู้เสนอ
          <input value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
        </label>
        <label>
          ราคารวม (บาท)
          <input inputMode="numeric" value={totalBidAmount} onChange={(e) => setTotalBidAmount(e.target.value.replace(/[^0-9]/g, ''))} />
        </label>
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, marginTop: 12 }}>
        หมายเหตุ
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>

      <div className="admin-actions" style={{ marginTop: 12 }}>
        <button
          className="btn-primary"
          disabled={busy || !vendorName.trim() || totalBidAmount.trim() === ''}
          onClick={() => onSave({ vendorName: vendorName.trim(), totalBidAmount: Number(totalBidAmount), notes: notes.trim() })}
        >
          บันทึก
        </button>
        <button className="btn-link" onClick={onCancel}>ยกเลิก</button>
      </div>
    </div>
  );
}

function CampaignTable({ campaigns, busy, onSave, onAdd, onDelete }: {
  campaigns: OpsCampaignDto[];
  busy: boolean;
  onSave: (id: string, patch: { actualAdsSpend: number | null; version: number }) => void;
  onAdd: (input: { name: string; phase?: string; targetAdsSpend?: number }) => void;
  onDelete: (c: OpsCampaignDto) => void;
}) {
  const [name, setName] = useState('');
  const [phase, setPhase] = useState('');
  const [target, setTarget] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const draftOf = (c: OpsCampaignDto) => drafts[c.id] ?? (c.actualAdsSpend === null ? '' : String(c.actualAdsSpend));

  return (
    <>
      {campaigns.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--dim)' }}>ยังไม่มีแคมเปญ</div>
      ) : (
        <OpsTable head={<><th>แคมเปญ</th><th>ช่วง</th><th className="num">งบที่ตั้ง</th><th>ใช้จริง (บาท)</th><th>ที่มาของตัวเลข</th><th /></>}>
              {campaigns.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td style={{ color: 'var(--dim)' }}>{c.phase || '—'}</td>
                  <td className="num" style={{ fontFamily: 'var(--mono)', fontSize: 12.5 }}>{baht(c.targetAdsSpend)}</td>
                  <td>
                    <input
                      aria-label={`ค่าโฆษณาที่ใช้จริงของ ${c.name}`}
                      inputMode="numeric"
                      style={{ width: 120 }}
                      placeholder="ยังไม่มีข้อมูล"
                      value={draftOf(c)}
                      onChange={(e) => setDrafts({ ...drafts, [c.id]: e.target.value.replace(/[^0-9]/g, '') })}
                    />
                  </td>
                  <td style={{ color: 'var(--dim)', fontSize: 12 }}>{c.spendSource || '—'}</td>
                  <td>
                    <RowActions>
                      <button
                        className="btn-link"
                        disabled={busy}
                        onClick={() => onSave(c.id, {
                          // Empty means unfilled, which is null — and null is not zero here.
                          actualAdsSpend: draftOf(c).trim() === '' ? null : Number(draftOf(c)),
                          version: c.version,
                        })}
                      >
                        บันทึก
                      </button>
                      <button className="btn-link" disabled={busy} onClick={() => onDelete(c)}>ลบ</button>
                    </RowActions>
                  </td>
                </tr>
              ))}
        </OpsTable>
      )}

      <div className="admin-actions" style={{ marginTop: 12, flexWrap: 'wrap' }}>
        <input aria-label="ชื่อแคมเปญใหม่" placeholder="ชื่อแคมเปญ" value={name} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 200 }} />
        <input aria-label="ช่วงของแคมเปญ" placeholder="ช่วง เช่น Early bird" value={phase} onChange={(e) => setPhase(e.target.value)} style={{ maxWidth: 160 }} />
        <input
          aria-label="งบโฆษณาที่ตั้งไว้"
          inputMode="numeric"
          placeholder="งบที่ตั้ง (บาท)"
          value={target}
          onChange={(e) => setTarget(e.target.value.replace(/[^0-9]/g, ''))}
          style={{ maxWidth: 150 }}
        />
        <button
          className="btn-primary"
          disabled={busy || !name.trim()}
          onClick={() => {
            onAdd({ name: name.trim(), phase: phase.trim(), targetAdsSpend: target.trim() === '' ? 0 : Number(target) });
            setName(''); setPhase(''); setTarget('');
          }}
        >
          เพิ่มแคมเปญ
        </button>
      </div>
    </>
  );
}
